import type { RouteRecordRaw } from 'vue-router'

// Route meta type definition
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresPremium?: boolean
    allowAnonymous?: boolean
    title?: string
  }
}

/**
 * The route table, kept in its own side-effect-free module.
 *
 * `router/index.ts` calls `createWebHistory()` at module scope, which touches
 * `window` — so importing it from a node-environment unit test throws before a
 * single assertion runs. Splitting the table out lets tests assert the
 * access-marker contract the onboarding guard reads ([70]) without a DOM.
 *
 * **Every route MUST declare `allowAnonymous` or `requiresAuth`.** They are not
 * complements, and a route with neither is invisible to both guards — that is
 * exactly how [70] shipped. `tests/unit/onboarding-routing.spec.ts` enforces it.
 */
export const routes: RouteRecordRaw[] = [
  // ---- Auth pages (no app layout) ----
  {
    path: '/auth',
    redirect: '/auth/login',
  },
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { allowAnonymous: true, title: 'Sign In' },
  },
  {
    path: '/auth/register',
    name: 'register',
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { allowAnonymous: true, title: 'Create Account' },
  },
  {
    path: '/auth/verify-email',
    name: 'verify-email',
    component: () => import('@/pages/VerifyEmail.vue'),
    meta: { allowAnonymous: true, title: 'Verify Email' },
  },
  {
    path: '/auth/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/ResetPassword.vue'),
    meta: { allowAnonymous: true, title: 'Reset Password' },
  },

  // ---- App layout wrapper (all pages with nav) ----
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/pages/Home.vue'),
        meta: { allowAnonymous: true, title: 'Move 78 - Run Your Way' },
      },
      {
        path: 'generate',
        name: 'generate',
        component: () => import('@/pages/Generate.vue'),
        meta: { allowAnonymous: true, title: 'Generate Route' },
      },
      {
        // Spec 047 US1/FR-001 — reachable logged out AND logged in.
        path: 'privacy',
        name: 'privacy',
        component: () => import('@/pages/PrivacyPolicy.vue'),
        meta: { allowAnonymous: true, title: 'Privacy Policy' },
      },
      {
        path: 'routes',
        name: 'routes',
        component: () => import('@/pages/Routes.vue'),
        meta: { requiresAuth: true, title: 'My Routes' },
      },
      {
        path: 'routes/:id',
        name: 'route-detail',
        component: () => import('@/pages/RouteDetail.vue'),
        meta: { requiresAuth: true, title: 'Route Details' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/pages/profile/ProfilePage.vue'),
        meta: { requiresAuth: true, title: 'Profile' },
      },
      {
        path: 'profile/consent-audit',
        name: 'consent-audit',
        component: () => import('@/pages/ConsentAuditLog.vue'),
        meta: { requiresAuth: true, title: 'Consent History' },
      },
      {
        path: 'events',
        name: 'events',
        component: () => import('@/pages/Events.vue'),
        meta: { requiresAuth: true, title: 'Events' },
      },
      {
        path: 'events/:id',
        name: 'event-detail',
        component: () => import('@/pages/EventDetail.vue'),
        meta: { requiresAuth: true, title: 'Event Details' },
      },
      {
        path: 'training',
        name: 'training',
        component: () => import('@/pages/Training.vue'),
        meta: { requiresAuth: true, title: 'Training Plans' },
      },
      {
        path: 'community',
        name: 'community',
        component: () => import('@/pages/Community.vue'),
        meta: { requiresAuth: true, title: 'Community Routes' },
      },
      {
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/pages/Onboarding.vue'),
        meta: { requiresAuth: true, title: 'Complete Your Profile' },
      },
      {
        path: ':pathMatch(.*)*',
        name: 'not-found',
        component: () => import('@/pages/NotFound.vue'),
        meta: { allowAnonymous: true, title: 'Page Not Found' },
      },
    ],
  },
]
